import { BarChart3, Target, CheckSquare, Activity, Zap, Users } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'The outcome is hard to measure',
    description:
      'Broad ambitions need a clear measure and target date before people can judge what progress means.',
    icon: BarChart3,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'The objective lacks a delivery path',
    description:
      'Even a well-written goal leaves questions about the strategies and projects that will support it.',
    icon: Target,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Daily work becomes detached',
    description:
      'Tasks are easier to prioritise when their connection to a company objective is visible.',
    icon: CheckSquare,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Measurable objectives',
    description: 'Capture your goal, how you will measure it, and the date you are working toward.',
    icon: Target,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'A path from goal to task',
    description: 'Link objectives to strategies, projects, tasks, and subtasks.',
    icon: CheckSquare,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Work progress',
    description: 'Follow project and task completion within the wider strategic hierarchy.',
    icon: BarChart3,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'A plan you can review',
    description: 'Create custom visual reports and export your plan as a PDF for discussion.',
    icon: Activity,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI objective drafts',
    description:
      'Get starting points for objectives based on the company direction and context you supply.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Clear ownership',
    description:
      'Assign responsibility for projects and tasks so people know which actions they own.',
    icon: Users,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function OkrSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Give measurable objectives a clear path to action."
      subheadline="Bring an OKR mindset into your strategic plan: define objectives with measures and target dates, then connect them to the strategies and work behind them."
      bullets={[
        'Objectives with measures and target dates',
        'Strategies connected to objectives',
        'Projects and tasks with owners',
        'Progress visible in the work hierarchy',
      ]}
      badgeText="OKR Software"
      problemTitle="An objective needs more than a place on a slide."
      problemSubtitle="When you work with OKRs, clarity depends on describing the outcome and understanding which actions support it."
      problemCards={problemCards}
      solutionTitle="Put measurable objectives inside the wider plan."
      solutionSubtitle="StratPlan pairs objective measures and dates with strategy, project, and task planning, giving your goals a practical route to action."
      solutionSteps={[
        {
          title: 'Describe the outcome',
          description:
            'Set objectives with measures and target dates. Use AI-generated drafts as starting points to refine.',
        },
        {
          title: 'Choose how to pursue it',
          description:
            'Develop strategies for each objective and connect the projects that will put those strategies into practice.',
        },
        {
          title: 'Make the work visible',
          description:
            'Assign owners and deadlines to tasks, update progress, and use the hierarchy to review the connection to your goals.',
        },
      ]}
      featuresTitle="The practical structure behind goal-focused work"
      features={features}
      finalCtaTitle="Make your objectives easier to act on."
      finalCtaSubtitle="Create your account and build a strategic plan that connects measurable goals to the work behind them."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="okr_software_landing"
      breadcrumbLabel="OKR Software"
    />
  );
}
